// src/components/layout/AdminLayout.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
    LayoutDashboard,
    Users,
    Settings,
    User,
    LogOut,
    Menu,
    X,
    UserCog,
    ChevronDown,
    Home,
    ClipboardList,
    BookOpen
} from 'lucide-react';
import { supabase } from '../../services/supabaseClient';
import { useAdminAuth } from '../../hooks/useAdminAuth';

// Interface mise à jour pour correspondre au type du hook useAdminAuth
interface AdminUser {
    id: string;
    email: string;
    role: string;
    name?: string;
    is_active?: boolean;
    can_read?: boolean;
    can_write?: boolean;
    can_delete?: boolean;
    can_manage_users?: boolean;
    created_at?: string;
}

interface AdminLayoutProps {
    children: React.ReactNode;
    user?: AdminUser | null;
    onLogout?: () => Promise<void>;
}

interface NavItem {
    name: string;
    href: string;
    icon: React.ComponentType<{ className?: string }>;
    permission?: 'read' | 'write' | 'delete' | 'manage_users';
    description?: string;
    roles?: string[]; // Nouveau : spécifier les rôles autorisés
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children, onLogout }) => {
    // Utiliser le hook directement dans AdminLayout
    const { user: hookUser } = useAdminAuth();

    // Utiliser l'utilisateur du hook en priorité
    const user = hookUser;

    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [profileMenuOpen, setProfileMenuOpen] = useState(false);
    const [logoError, setLogoError] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    // Ref pour le dropdown profil
    const profileDropdownRef = useRef<HTMLDivElement>(null);

    const location = useLocation();
    const navigate = useNavigate();

    // Fermer les menus quand on change de page
    useEffect(() => {
        setSidebarOpen(false);
        setProfileMenuOpen(false);
    }, [location.pathname]);

    // Gérer les clics en dehors du dropdown profil
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target as Node)) {
                setProfileMenuOpen(false);
            }
        };

        if (profileMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }

        return undefined;
    }, [profileMenuOpen]);

    // Rediriger les viewers vers le dashboard s'ils tentent d'accéder à d'autres pages
    useEffect(() => {
        if (user && user.role === 'viewer') {
            const allowedPaths = ['/admin/dashboard', '/admin', '/admin/', '/admin/profile'];
            const currentPath = location.pathname;

            if (!allowedPaths.includes(currentPath)) {
                navigate('/admin/dashboard', { replace: true });
            }
        }
    }, [user, location.pathname, navigate]);

    // Fonction de déconnexion avec gestion d'état
    const handleLogout = async (e?: React.MouseEvent) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (isLoggingOut) return;

        try {
            setIsLoggingOut(true);
            setProfileMenuOpen(false);

            console.log('🔄 Déconnexion en cours...');

            if (onLogout) {
                await onLogout();
            } else {
                const { error } = await supabase.auth.signOut();
                if (error) {
                    console.error('❌ Erreur Supabase signOut:', error);
                    throw error;
                }
                navigate('/admin/login');
            }

            console.log('✅ Déconnexion réussie');

        } catch (error) {
            console.error('❌ Erreur lors de la déconnexion:', error);
            setIsLoggingOut(false);
            alert('Erreur lors de la déconnexion. Veuillez réessayer.');
        }
    };

    // Configuration de la navigation avec restrictions par rôle
    const navigation: NavItem[] = [
        {
            name: 'Dashboard',
            href: '/admin/dashboard',
            icon: LayoutDashboard,
            permission: 'read',
            description: 'Vue d\'ensemble et statistiques',
            roles: ['admin', 'viewer'] // Accessible aux deux rôles
        },
        {
            name: 'Membres',
            href: '/admin/members',
            icon: Users,
            permission: 'read',
            description: 'Gestion des membres UDB',
            roles: ['admin'] // Accessible seulement aux admins
        },
        {
            name: 'Enquêtes',
            href: '/admin/surveys',
            icon: ClipboardList,
            permission: 'write',
            description: 'Gestion des enquêtes et sondages',
            roles: ['admin'] // Accessible seulement aux admins
        },
        {
            name: 'Formations',
            href: '/admin/formations',
            icon: BookOpen,
            permission: 'write',
            description: 'Gestion des formations vidéo',
            roles: ['admin'] // Accessible seulement aux admins
        },
        {
            name: 'Utilisateurs Admin',
            href: '/admin/users',
            icon: UserCog,
            permission: 'manage_users',
            description: 'Gestion des administrateurs',
            roles: ['admin'] // Accessible seulement aux admins
        },
        {
            name: 'Paramètres',
            href: '/admin/settings',
            icon: Settings,
            permission: 'write',
            description: 'Configuration du système',
            roles: ['admin'] // Accessible seulement aux admins
        }
    ];

    // Fonction pour vérifier si un élément de navigation doit être affiché
    const shouldShowNavItem = (item: NavItem) => {
        if (!user) {
            return false;
        }

        // Vérifier d'abord le rôle
        if (item.roles && !item.roles.includes(user.role)) {
            return false;
        }

        // Ensuite vérifier les permissions si spécifiées
        if (item.permission) {
            const hasPermission = (() => {
                switch (item.permission) {
                    case 'read':
                        return user.can_read ?? (user.role === 'admin');
                    case 'write':
                        return user.can_write ?? (user.role === 'admin');
                    case 'delete':
                        return user.can_delete ?? (user.role === 'admin');
                    case 'manage_users':
                        return user.can_manage_users ?? (user.role === 'admin');
                    default:
                        return user.role === 'admin';
                }
            })();

            if (!hasPermission) {
                return false;
            }
        }

        return true;
    };

    // Fonction pour vérifier si un lien est actif
    const isActiveLink = (href: string) => {
        if (href === '/admin/dashboard') {
            return location.pathname === '/admin/dashboard' ||
                location.pathname === '/admin' ||
                location.pathname === '/admin/';
        }
        return location.pathname.startsWith(href);
    };

    // Fonction pour obtenir le titre de la page
    const getPageTitle = () => {
        const path = location.pathname;
        if (path === '/admin/dashboard' || path === '/admin' || path === '/admin/')
            return 'Dashboard';
        if (path === '/admin/members')
            return 'Gestion des Membres';
        if (path.startsWith('/admin/members/'))
            return 'Détails du Membre';
        if (path === '/admin/surveys')
            return 'Gestion des Enquêtes';
        if (path === '/admin/formations')
            return 'Gestion des Formations';
        if (path === '/admin/users')
            return 'Gestion des Utilisateurs';
        if (path === '/admin/settings')
            return 'Paramètres';
        if (path === '/admin/profile')
            return 'Mon Profil';
        return 'Administration';
    };

    // Fonction pour obtenir la description de la page selon le rôle
    const getPageDescription = () => {
        const path = location.pathname;

        if (user?.role === 'viewer') {
            if (path === '/admin/dashboard' || path === '/admin' || path === '/admin/')
                return 'Vue d\'ensemble en lecture seule';
            if (path === '/admin/profile')
                return 'Informations de votre profil';
            return 'Accès en lecture seule';
        }

        // Descriptions pour les admins
        if (path === '/admin/dashboard' || path === '/admin' || path === '/admin/')
            return 'Vue d\'ensemble de l\'administration';
        if (path === '/admin/members')
            return 'Gérer les membres et leurs informations';
        if (path.startsWith('/admin/members/'))
            return 'Informations détaillées du membre';
        if (path === '/admin/surveys')
            return 'Créer et gérer les enquêtes et sondages';
        if (path === '/admin/formations')
            return 'Créer et gérer les formations vidéo';
        if (path === '/admin/users')
            return 'Gérer les comptes administrateurs';
        if (path === '/admin/settings')
            return 'Configuration et paramètres système';
        return 'Panneau d\'administration';
    };

    return (
        <div className="min-h-screen bg-gray-50 flex">
            {/* Sidebar mobile overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={`fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 lg:flex lg:flex-col ${
                sidebarOpen ? 'translate-x-0' : '-translate-x-full'
            }`}>
                {/* Sidebar header */}
                <div className="flex items-center justify-between h-16 px-6 bg-white text-black">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            {!logoError ? (
                                <img
                                    src="/logo192.png"
                                    alt="UDB Logo"
                                    className="w-14 h-14 rounded-xl bg-white bg-opacity-20 backdrop-blur-sm p-1"
                                    onError={() => setLogoError(true)}
                                />
                            ) : (
                                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                                    <span className="text-black font-bold text-lg">UDB</span>
                                </div>
                            )}
                        </div>
                        <div className="ml-3">
                            <h1 className="text-xl font-bold">Administration</h1>
                            <p className="text-black text-xs">
                                UDB {user?.role === 'viewer' && '(Lecture seule)'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="lg:hidden p-2 rounded-lg text-white hover:bg-white hover:bg-opacity-20 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Navigation */}
                <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
                    {navigation.map((item) => {
                        const Icon = item.icon;
                        const active = isActiveLink(item.href);
                        const shouldShow = shouldShowNavItem(item);

                        if (!shouldShow) {
                            return null;
                        }

                        return (
                            <Link
                                key={item.name}
                                to={item.href}
                                className={`group flex items-center px-3 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
                                    active
                                        ? 'bg-blue-50 text-blue-700 shadow-sm border-l-4 border-blue-600'
                                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                }`}
                                onClick={() => setSidebarOpen(false)}
                            >
                                <Icon className={`mr-3 h-5 w-5 transition-colors ${
                                    active ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'
                                }`} />
                                <div className="flex-1">
                                    <div className="font-medium">{item.name}</div>
                                    {item.description && (
                                        <div className="text-xs text-gray-500 mt-0.5">
                                            {item.description}
                                        </div>
                                    )}
                                </div>
                            </Link>
                        );
                    })}
                </nav>

                {/* Sidebar footer */}
                <div className="border-t border-gray-200 p-4 space-y-2">
                    <a
                        href="/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                    >
                        <Home className="w-4 h-4 mr-3 text-gray-400" />
                        Voir le site public
                    </a>

                    <button
                        onClick={handleLogout}
                        disabled={isLoggingOut}
                        className="w-full flex items-center px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <LogOut className={`w-4 h-4 mr-3 ${isLoggingOut ? 'animate-spin' : ''}`} />
                        {isLoggingOut ? 'Déconnexion...' : 'Se déconnecter'}
                    </button>
                </div>
            </aside>

            {/* Main content */}
            <div className="flex-1 flex flex-col min-w-0">
                {/* Top bar */}
                <header className="sticky top-0 z-30 bg-white shadow-sm border-b border-gray-200 flex-shrink-0">
                    <div className="flex items-center justify-between h-16 px-6">
                        {/* Left side */}
                        <div className="flex items-center">
                            {/* Mobile menu button */}
                            <button
                                onClick={() => setSidebarOpen(true)}
                                className="lg:hidden p-2 rounded-lg text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-colors mr-4"
                            >
                                <Menu className="w-6 h-6" />
                            </button>

                            {/* Logo mobile */}
                            <div className="lg:hidden mr-4">
                                {!logoError ? (
                                    <img
                                        src="/logo192.png"
                                        alt="UDB Logo"
                                        className="w-8 h-8 rounded-lg"
                                        onError={() => setLogoError(true)}
                                    />
                                ) : (
                                    <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                                        <span className="text-white font-bold text-sm">UDB</span>
                                    </div>
                                )}
                            </div>

                            {/* Page title */}
                            <div>
                                <h1 className="text-xl font-semibold text-gray-900">
                                    {getPageTitle()}
                                    {user?.role === 'viewer' && (
                                        <span className="ml-2 text-sm bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                                            Lecture seule
                                        </span>
                                    )}
                                </h1>
                                <p className="text-sm text-gray-500 hidden sm:block">
                                    {getPageDescription()}
                                </p>
                            </div>
                        </div>

                        {/* Right side - Profile */}
                        <div className="flex items-center space-x-4">
                            {/* Profile dropdown */}
                            <div className="relative" ref={profileDropdownRef}>
                                <button
                                    onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                                    className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                                    disabled={isLoggingOut}
                                >
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                                        user?.role === 'viewer'
                                            ? 'bg-gradient-to-br from-yellow-500 to-yellow-600'
                                            : 'bg-gradient-to-br from-blue-500 to-blue-600'
                                    }`}>
                                        <User className="w-4 h-4 text-white" />
                                    </div>
                                    <div className="hidden sm:block text-left">
                                        <div className="text-sm font-medium text-gray-900 truncate max-w-32">
                                            {user?.name || user?.email || 'Utilisateur'}
                                        </div>
                                        <div className="text-xs text-gray-500 capitalize">
                                            {user?.role === 'admin' ? 'Administrateur' : 'Lecteur'}
                                        </div>
                                    </div>
                                    <ChevronDown className="ml-2 w-4 h-4 text-gray-400" />
                                </button>

                                {/* Dropdown menu */}
                                {profileMenuOpen && (
                                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                                        <div className="px-4 py-3 border-b border-gray-100">
                                            <p className="text-sm font-medium text-gray-900 truncate">
                                                {user?.name || user?.email}
                                            </p>
                                            <p className="text-xs text-gray-500 capitalize mt-1">
                                                {user?.role === 'admin' ? 'Administrateur' : 'Lecteur'}
                                                {user?.role === 'viewer' && ' (accès limité)'}
                                            </p>
                                        </div>

                                        <div className="py-1">
                                            <Link
                                                to="/admin/profile"
                                                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                                onClick={() => setProfileMenuOpen(false)}
                                            >
                                                <User className="w-4 h-4 mr-3 text-gray-400" />
                                                Mon Profil
                                            </Link>

                                            {/* Masquer les paramètres pour les viewers */}
                                            {user?.role === 'admin' && (
                                                <Link
                                                    to="/admin/settings"
                                                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                                    onClick={() => setProfileMenuOpen(false)}
                                                >
                                                    <Settings className="w-4 h-4 mr-3 text-gray-400" />
                                                    Paramètres
                                                </Link>
                                            )}
                                        </div>

                                        <div className="border-t border-gray-100 py-1">
                                            <button
                                                onClick={handleLogout}
                                                disabled={isLoggingOut}
                                                className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-left"
                                            >
                                                <LogOut className={`w-4 h-4 mr-3 ${isLoggingOut ? 'animate-spin' : ''}`} />
                                                {isLoggingOut ? 'Déconnexion...' : 'Se déconnecter'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </header>

                {/* Page content */}
                <main className="flex-1 overflow-auto">
                    <div className="p-6">
                        {children}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default AdminLayout;
